/**
 * What the person detail page says about a person, derived from the person
 * payload alone: the office lines, Key facts, and the finance window. Only
 * facts the payload carries are shown — a fact with no data is left out rather
 * than rendered as a dash.
 */

import { formatDate } from '../../utils/dateUtils';
import { formatDistrict } from '../../utils/districtDisplay';
import { SYSTEM_MILESTONE_TYPES } from '../../constants/elections';
import { isFederalFinance } from './types';
import type { CampaignFinanceData, CandidateData, LegislatorData, PersonData } from './types';

export interface KeyFact {
  label: string;
  value: string;
  /** Set on the Committee fact: the value renders as a CommitteeLink (PRD §5.12). */
  committee?: { coId: string | null };
  /** Set on a federal Committee fact: the value links out to the committee on fec.gov. */
  href?: string;
}

/** "State senator" / "State representative" — the sitting legislator's office. */
export function legislatorOfficeTitle(chamber: string | null | undefined): string {
  return /senate/i.test(chamber ?? '') ? 'State senator' : 'State representative';
}

/** "Colorado Senate · CO SD-04" */
export function legislatorSeat(legislator: Pick<LegislatorData, 'chamber' | 'district'>): string {
  const body = /senate/i.test(legislator.chamber ?? '') ? 'Colorado Senate' : 'Colorado House';
  const district = formatDistrict(null, String(legislator.district ?? ''), legislator.chamber);
  return district ? `${body} · ${district}` : body;
}

/**
 * The office a candidate is running for, once: some office_sought values carry
 * the district after " · ", and a district display is added only when the
 * office name does not already contain one.
 */
export function candidateOffice(candidate: Pick<CandidateData, 'office_sought' | 'district_display'>): string {
  const office = (candidate.office_sought ?? '').split(' · ')[0];
  return candidate.district_display && !/\d/.test(office) ? `${office} · ${candidate.district_display}` : office;
}

/** The cycle's general election date (YYYY-MM-DD…), when the cycle records one. */
export function generalElectionDate(candidate: Pick<CandidateData, 'election_cycle'>): string | null {
  return candidate.election_cycle?.milestones.find((m) => m.type === SYSTEM_MILESTONE_TYPES.GENERAL)?.date ?? null;
}

/** Key facts for the hero, in role order (legislator, candidate, officeholder). */
export function personKeyFacts(person: PersonData): KeyFact[] {
  const facts: KeyFact[] = [];
  const add = (label: string, value: string | null | undefined) => {
    if (value && value.trim()) facts.push({ label, value: value.trim() });
  };

  const legislator = person.roles.includes('legislator') ? person.legislator : null;
  const candidate = person.roles.includes('candidate') ? person.candidate : null;
  const officeholder = person.roles.includes('officeholder') ? person.officeholder : null;

  if (legislator) add('Current office', legislatorSeat(legislator));

  if (candidate) {
    add('Running for', candidateOffice(candidate));
    if (candidate.is_incumbent) add('Incumbent in race', 'Yes, seeking re-election');
    else add('Incumbent in race', candidate.seat_incumbent_name);
    const general = generalElectionDate(candidate);
    if (general) add('General election', formatDate(general));
    if (candidate.affidavit_filed_date) add('Declared', formatDate(candidate.affidavit_filed_date));
    const committeeName = candidate.campaign_finance_committee_name?.trim();
    const finance = person.campaign_finance;
    if (isFederalFinance(finance)) {
      // A federal committee is FEC's: it links out to fec.gov, never the TRACER card.
      const name = finance.committee?.name?.trim() || committeeName;
      if (name) facts.push({ label: 'Committee', value: name, href: finance.committee?.fec_url });
    } else if (committeeName) {
      // The finance payload's co_id belongs to this committee only when the names agree;
      // otherwise the card resolves the name (exact match, never a guess).
      const coId = finance?.co_id && finance.committee_name?.trim().toLowerCase() === committeeName.toLowerCase()
        ? finance.co_id
        : null;
      facts.push({ label: 'Committee', value: committeeName, committee: { coId } });
    }
  }

  // An officeholder who also sits in the legislature is described by that seat.
  if (officeholder && !legislator) {
    add('Title', officeholder.title);
    add('Organization', officeholder.organization);
    add('Jurisdiction', officeholder.jurisdiction);
    if (officeholder.start_date) {
      add('In office since', formatDate(officeholder.start_date, { month: 'long', day: undefined, year: 'numeric' }));
    }
    if (officeholder.term_end) add('Term ends', formatDate(officeholder.term_end));
  }

  // One row of five: the finance committee is the first to give way — the
  // Campaign finance card names it too.
  return facts.length > 5 ? facts.filter((fact) => fact.label !== 'Committee') : facts;
}

export interface FinanceSummary {
  years: number[];
  /** "2026" or "2025–2026" */
  label: string;
  total: number;
  count: number;
  average: number;
}

/**
 * The fundraising the page summarises: for a candidate, the two calendar years
 * that make up their election cycle (contributions are bucketed by calendar
 * year); otherwise the most recent year with any. Null when there is no data.
 */
export function financeSummary(finance: CampaignFinanceData, electionYear?: number | null): FinanceSummary | null {
  const byYear = finance.finance_by_year ?? {};
  const all = Object.keys(byYear).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (all.length === 0) return null;

  let years = electionYear ? all.filter((y) => y === electionYear || y === electionYear - 1) : [];
  if (years.length === 0) years = [all[all.length - 1]];

  const total = years.reduce((sum, y) => sum + (byYear[y]?.total_raised ?? 0), 0);
  const count = years.reduce((sum, y) => sum + (byYear[y]?.contribution_count ?? 0), 0);
  return {
    years,
    label: years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : String(years[0]),
    total,
    count,
    average: count > 0 ? Math.round(total / count) : 0,
  };
}

/**
 * The finance explorer: the committee's dashboard by TRACER id (PRD §5.12), or,
 * without one, a search for the person. Never a committee_name prefill — names
 * are not unique (committeeLinkConventions.test.ts).
 */
export function financeExplorerHref(coId: string | null | undefined, personName: string): string {
  return coId
    ? `/campaign-finance?co_id=${encodeURIComponent(coId)}`
    : `/campaign-finance?candidate=${encodeURIComponent(personName)}&start_date=2000-01-01`;
}
